import { makeStyles, Theme } from '@material-ui/core'

import { ButtonProps } from './ButtonProps'

const borderWidth = 1

const getColor = (palette: Theme['palette'], props: ButtonProps): string => {
  const variant = props.variant as string
  let color = 'inherit'
  if (['primary', 'info', 'success', 'warning', 'error'].includes(variant)) {
    // @ts-expect-error - todo.
    color = palette[variant].contrastText
  }
  if (variant === 'secondary') {
    color = palette[variant].main
    /**
     * Temporary fix for dark theme buttons.
     * Dark theme will be addressed in ch-11596 and the following if block should be removed
     */
    if (palette.type === 'dark') {
      color = 'black'
    }
  }
  if (variant === 'tertiary') {
    color = palette.secondary.contrastText
  }
  if (variant === 'text') {
    color = palette.info.main
  }
  return color
}

const getColorHover = (palette: Theme['palette'], props: ButtonProps): string | undefined => {
  let color
  if (props.variant === 'secondary') {
    color = palette.secondary.contrastText
  }
  return color
}

const getBackground = (palette: Theme['palette'], props: ButtonProps): string => {
  let backgroundColor = 'inherit'
  if (['info', 'success', 'warning', 'error'].includes(props.variant as string)) {
    // @ts-expect-error - todo.
    backgroundColor = palette[props.variant as string].main
  }
  if (props.variant === 'primary') {
    backgroundColor = `linear-gradient(97.36deg, ${palette.primary.gradientStart} 17.03%, ${palette.primary.gradientEnd} 72.96%)`
  }
  if (props.variant === 'secondary') {
    backgroundColor = 'white'
  }
  if (props.variant === 'tertiary') {
    backgroundColor = palette.secondary.main
  }
  return backgroundColor
}

const getBackgroundHover = (palette: Theme['palette'], props: ButtonProps): string => {
  let backgroundHoverColor = 'inherit'
  if (['primary', 'info', 'success', 'warning', 'error'].includes(props.variant as string)) {
    // @ts-expect-error - todo.
    backgroundHoverColor = palette[props.variant as string].dark
  }
  if (props.variant === 'secondary') {
    backgroundHoverColor = palette.secondary.main
  }
  if (props.variant === 'tertiary') {
    backgroundHoverColor = palette.secondary.light
  }
  if (props.variant === 'text' || props.variant === 'ghost') {
    backgroundHoverColor = palette.action.hover
  }
  return backgroundHoverColor
}

export const buttonTypography = {
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
}

const isPopoverTheme = (themeName: string) => themeName === 'Popover'

export const useButtonStyles = makeStyles<Theme, ButtonProps>(({ palette, spacing, themeName }) => ({
  // Props callbacks must wrap the whole rule. Nested selectors like `&:hover` ignore
  // property-level `(props) =>` callbacks in MUI v4 / JSS.
  root: (props) => ({
    minWidth: 'unset',
    color: isPopoverTheme(themeName) ? undefined : getColor(palette, props),
    background: isPopoverTheme(themeName) ? undefined : getBackground(palette, props),
    backgroundColor: isPopoverTheme(themeName)
      ? undefined
      : props.variant === 'primary'
      ? palette.primary.dark
      : getBackground(palette, props),
    borderRadius: spacing(6),
    transition: 'all 200ms ease-in-out',
    '&:hover': {
      color: isPopoverTheme(themeName) ? undefined : getColorHover(palette, props),
      background: isPopoverTheme(themeName) ? undefined : getBackgroundHover(palette, props),
    },
  }),
  disabled: {
    '&$root': {
      background: palette.action.disabled,
    },
  },
  contained: {
    padding: spacing(1, 2),
  },
  outlined: {
    padding: `calc(${spacing(1)}px - ${borderWidth}px) calc(${spacing(2)}px - ${borderWidth}px)`,
    '&$sizeSmall': {
      padding: `calc(${spacing(0.5)}px - ${borderWidth}px) calc(${spacing(2)}px - ${borderWidth}px)`,
    },
    '&$sizeLarge': {
      padding: `calc(${spacing(1.5)}px - ${borderWidth}px) calc(${spacing(2)}px - ${borderWidth}px)`,
    },
  },
  text: {
    padding: spacing(1, 2),
  },
  label: {
    ...buttonTypography,
    textTransform: (props) => props.textTransform,
  },
  sizeSmall: {
    padding: spacing(0.5, 2),
  },
  sizeLarge: {
    padding: spacing(1.5, 2),
  },
}))
